import { useRef, useEffect, useState } from 'react'; // Added useState for runtime validation
import { Star, Quote, ChevronLeft, ChevronRight, User } from 'lucide-react'; // Added User icon
import { Link } from 'react-router-dom';

type Testimonial = {
  id: number;
  name: string;
  role: string;
  image: string;
  rating: number;
  text: string;
};

const testimonials = [
  {
    id: 1,
    name: 'Ananya Verma',
    role: 'Software Engineer',
    image: '',
    rating: 5,
    text: 'trueAdviso helped me navigate a critical transition. The expert I consulted provided personalized guidance that made all the difference. Highly recommend!',
  },
  {
    id: 2,
    name: 'Rahul Sharma',
    role: 'Retired School Principal',
    image: '',
    rating: 5,
    text: 'I was skeptical about online astrology, but the experts here are professional and genuinely insightful. The seamless interface makes it easy even for someone like me who isn\'t very tech-savvy!',
  },
  {
    id: 3,
    name: 'Priya Malhotra',
    role: 'Startup Founder',
    image: '',
    rating: 5,
    text: 'Navigating legal paperwork for my startup was overwhelming until I found a consultant on trueAdviso. The instant video call saved me days of back-and-forth emails. It’s the most reliable platform I’ve used!',
  },
  {
    id: 4,
    name: 'Arjun Patel',
    role: 'Software Engineer',
    image: '', // Test empty fallback target
    rating: 5,
    text: 'Being away from home, I often need quick guidance on various life matters. trueAdviso is my go-to for everything—from legal advice to personal wellness. It’s truly a one-stop-shop for reliable experts!',
  },
  {
    id: 5,
    name: 'Sneha Reddy',
    role: 'Homemaker',
    image: '',
    rating: 5,
    text: 'trueAdviso connected me with the right experts at the right time. The guidance I received was practical and immediately applicable. Highly satisfied!',
  },
];

const displayTestimonials = [...testimonials, ...testimonials];

const TestimonialCard = ({ testimonial }: { testimonial: Testimonial }) => {
  // 🟢 NEW: State layer to handle runtime broken links
  const [isImgBroken, setIsImgBroken] = useState(false);

  // Check if string is empty or just whitespace
  const hasValidImageString = testimonial.image && testimonial.image.trim() !== '';

  return (
    <div className="h-full bg-white rounded-2xl p-6 sm:p-8 shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-100 flex flex-col">
      {/* Quote Icon */}
      <div className="w-12 h-12 bg-blue-50 rounded-full flex items-center justify-center mb-6 shrink-0">
        <Quote className="w-6 h-6 text-blue-600" />
      </div>

      {/* Rating */}
      <div className="flex gap-1 mb-4 shrink-0">
        {[...Array(testimonial.rating)].map((_, i) => (
          <Star
            key={i}
            className="w-5 h-5 text-yellow-500 fill-yellow-500"
          />
        ))}
      </div>

      {/* Testimonial Text */}
      <p className="text-sm sm:text-base text-gray-700 mb-6 leading-relaxed flex-grow">
        "{testimonial.text}"
      </p>

      {/* User Info Wrapper Segment */}
      <div className="flex items-center gap-4 pt-6 border-t border-gray-50 shrink-0">
        
        {/* 🟢 FIXED: Safe conditional check avoiding image crash templates */}
        {!hasValidImageString || isImgBroken ? (
          <div className="w-14 h-14 rounded-full bg-slate-100 border border-slate-200 flex items-center justify-center text-slate-400 shrink-0 select-none">
            <User className="w-6 h-6" />
          </div>
        ) : (
          <img
            src={testimonial.image}
            alt={testimonial.name}
            onError={() => setIsImgBroken(true)} // Intercept broken links on execution
            className="w-14 h-14 rounded-full object-cover shrink-0"
          />
        )}

        <div>
          <div className="text-sm sm:text-base font-semibold text-gray-900">
            {testimonial.name}
          </div>
          <div className="text-xs sm:text-sm text-gray-500">
            {testimonial.role}
          </div>
        </div>
      </div>
    </div>
  );
};

export function TestimonialsHomepage() {
  const carouselRef = useRef<HTMLDivElement>(null);
  const animationRef = useRef<number | null>(null);
  const isHoveredRef = useRef(false);

  useEffect(() => {
    if (!carouselRef.current) return;

    const scrollSpeed = 0.5; 
    const carousel = carouselRef.current;

    const smoothScroll = () => {
      if (!isHoveredRef.current && carousel) {
        if (carousel.scrollWidth > carousel.clientWidth) {
          carousel.scrollLeft += scrollSpeed;
          if (carousel.scrollLeft >= carousel.scrollWidth - carousel.clientWidth - 1) {
            carousel.scrollLeft = 0;
          }
        }
      }
      animationRef.current = requestAnimationFrame(smoothScroll);
    };

    animationRef.current = requestAnimationFrame(smoothScroll);

    return () => {
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
    };
  }, []);

  const scrollCarousel = (direction: 'left' | 'right') => {
    if (carouselRef.current) {
      const cardWidth = (carouselRef.current.firstChild as HTMLElement)?.clientWidth || 350;
      const scrollAmount = direction === 'left' ? -(cardWidth + 24) : (cardWidth + 24); 
      carouselRef.current.scrollBy({ left: scrollAmount, behavior: 'smooth' });
    }
  };

  return (
    <section className="py-12 sm:py-20 bg-gradient-to-b from-blue-50 via-white to-blue-100/40 overflow-hidden">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6">
        
        <div className="text-center mb-10 sm:mb-16">
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
            What Our Users Say
          </h2>
          <p className="text-base sm:text-lg text-gray-600 max-w-2xl mx-auto">
            Real stories from people who found their path with trueAdviso
          </p>
        </div>

        <div 
          className="relative group"
          onMouseEnter={() => { isHoveredRef.current = true; }}
          onMouseLeave={() => { isHoveredRef.current = false; }}
          onTouchStart={() => { isHoveredRef.current = true; }}
          onTouchEnd={() => { isHoveredRef.current = false; }}
        >
          {/* LEFT ARROW */}
          <button 
            onClick={() => scrollCarousel('left')}
            className="absolute left-0 top-1/2 -translate-y-1/2 z-10 hidden sm:flex p-3 rounded-full bg-white border border-gray-100 text-gray-600 hover:text-blue-600 hover:border-blue-200 shadow-[0_4px_12px_rgba(0,0,0,0.1)] transition-all transform -translate-x-1/2"
            aria-label="Previous testimonial"
          >
            <ChevronLeft className="w-6 h-6" />
          </button>

          {/* SCROLLING CONTAINER */}
          <div 
            ref={carouselRef}
            className="flex overflow-x-auto gap-4 sm:gap-6 pb-8 pt-4 px-4 sm:px-8 [&::-webkit-scrollbar]:hidden items-stretch"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
          >
            {displayTestimonials.map((testimonial, index) => (
              <div 
                key={`${testimonial.id}-${index}`} 
                className="flex-none w-[280px] sm:w-[380px]"
              >
                <TestimonialCard testimonial={testimonial} />
              </div>
            ))}
          </div>

          {/* RIGHT ARROW */}
          <button 
            onClick={() => scrollCarousel('right')}
            className="absolute right-0 top-1/2 -translate-y-1/2 z-10 hidden sm:flex p-3 rounded-full bg-white border border-gray-100 text-gray-600 hover:text-blue-600 hover:border-blue-200 shadow-[0_4px_12px_rgba(0,0,0,0.1)] transition-all transform translate-x-1/2"
            aria-label="Next testimonial"
          >
            <ChevronRight className="w-6 h-6" />
          </button>
        </div>

        {/* CTA */}
        <div className="mt-8 sm:mt-12 text-center">
          <p className="text-sm sm:text-base text-gray-600 mb-5">
            Join thousands of satisfied users
          </p>
          <Link 
            to="/expert-signup"
            className="inline-block px-8 sm:px-10 py-3.5 sm:py-4 bg-gradient-to-r from-slate-900 to-slate-800 text-white rounded-xl hover:shadow-lg hover:-translate-y-1 transition-all text-sm sm:text-base font-semibold"
          >
            Start Your Journey Today
          </Link>
        </div>

      </div>
    </section>
  ); 
}

export default TestimonialsHomepage;